import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BookRequestPayloadType, User } from "../types/bookRequestTypes";
import { showToast } from "../../../../core/utils/alert";
import { all_routes } from "../../../router/all_routes";
import CommonSelect from "../../../../core/common/commonSelect";
import { useCreateBookRequest } from "../hooks/useCreateBookRequest";
import { useGetStudentByIdQuery } from "../../../peoples/students/api/studentApi";


const CreateBookRequest: React.FC = () => {
  const { addBookRequest, isLoading } = useCreateBookRequest();
  const navigate = useNavigate();
  const routes = all_routes;
  const [searchParams] = useSearchParams();

  // Get URL parameters
  const studentId = searchParams.get("student_id") || "";
  const courseId = searchParams.get("course_id") || "";
  const stageId = searchParams.get("stage_id") || "";

  // Use the direct query hook instead of going through useBookRequest
  const { data: studentResponse, refetch } = useGetStudentByIdQuery(
    { id: studentId },
    {
      skip: !studentId,
      refetchOnMountOrArgChange: true,
    }
  );

  const studentData = studentResponse?.data?.student;
  const attendanceCount = studentData?.attendance_present_count || 0;
  const isStudentActive = studentData?.status === "active";

  // Check invoice status
  const hasInvoices = studentData?.invoices && studentData.invoices.length > 0;
  const hasCompletedRegistrationFee =
    studentData?.course?.stage?.name === "Stage 1"
      ? studentData?.invoices?.some(
        (invoice) =>
          invoice.type === "registrationfee" && invoice.status === "completed"
      )
      : true;
  const hasCompletedCourseFee = studentData?.invoices?.some(
    (invoice) => invoice.type === "coursefee" && invoice.status === "completed"
  );
  const hasRequiredInvoices = hasCompletedCourseFee;

  // Initialize form data
  const initialRequestState: Omit<
    BookRequestPayloadType,
    "status" | "request_date"
  > = {
    student_id: parseInt(studentId) || 0,
    course_id: parseInt(courseId) || 0,
    stage_id: parseInt(stageId) || 0,
  };

  const [formData, setFormData] =
    useState<Omit<BookRequestPayloadType, "status" | "request_date">>(
      initialRequestState
    );

  // Add this function to generate stage options
  const getStageOptions = () => {
    if (studentData?.course?.stage) {
      const currentStage = studentData.course.stage.id;
      const currentStageName = studentData.course.stage.name;
      const totalStages = studentData.course.stages;
      const currentStageNumber = parseInt(currentStageName.split(" ")[1]);

      if (currentStageNumber === totalStages) {
        return [{ value: "last", label: "You are in the final stage" }];
      } else {
        const nextStage = currentStage + 1;
        const nextStageNumber = currentStageNumber + 1;
        return [
          { value: nextStage.toString(), label: `Level ${nextStageNumber}` },
        ];
      }
    }
    return [];
  };

  // Calculate attendance requirements after formData is declared
  const hasMinimumAttendance = attendanceCount >= 7;

  // Add this function to check if selected stage is Stage 1
  const isSelectedStageStage1 = () => {
    return false; // Students can only request the next level, so they can never request Level 1
  };

  // Get book request status and message
  const getBookRequestStatus = () => {
    if (!studentData?.bookRequests || !studentData.bookRequests.length) {
      return null;
    }

    const nextStageId = formData.stage_id + 1;

    // First check if there's a completed request
    const completedRequest = studentData.bookRequests.find(
      (request) =>
        request.stage_id === nextStageId && request.status === "completed"
    );

    if (completedRequest) {
      // Get stage name from the request
      const stageName = completedRequest.stage?.name || `Level ${nextStageId}`;
      return (
        <div className="alert alert-primary mb-3" role="alert">
          <div className="d-flex">
            <i className="ti ti-info-circle mt-2 me-2"></i>
            <div>
              <h6 className="alert-heading mb-1">Books Already Distributed</h6>
              <p className="mb-0">
                Books for {stageName} have already been provided to this student
                on {completedRequest.requested_on}. New books for the same level
                cannot be requested again.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Then check if there's an approved request
    const approvedRequest = studentData.bookRequests.find(
      (request) =>
        request.stage_id === nextStageId && request.status === "approved"
    );

    if (approvedRequest) {
      // Get stage name from the request
      const stageName = approvedRequest.stage?.name || `Level ${nextStageId}`;
      return (
        <div className="alert alert-success mb-3" role="alert">
          <div className="d-flex">
            <i className="ti ti-check mt-2 me-2"></i>
            <div>
              <h6 className="alert-heading mb-1">Request Approved</h6>
              <p className="mb-0">
                A book request for {stageName} has already been approved on{" "}
                {approvedRequest.requested_on}. The books will be provided to
                the student soon.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Finally check if there's a pending request
    const pendingRequest = studentData.bookRequests.find(
      (request) =>
        request.stage_id === nextStageId && request.status === "pending"
    );

    if (pendingRequest) {
      // Get stage name from the request
      const stageName = pendingRequest.stage?.name || `Level ${nextStageId}`;
      return (
        <div className="alert alert-warning mb-3" role="alert">
          <div className="d-flex">
            <i className="ti ti-alert-triangle mt-2 me-2"></i>
            <div>
              <h6 className="alert-heading mb-1">Pending Request</h6>
              <p className="mb-0">
                A book request for {stageName} is already pending (requested on{" "}
                {pendingRequest.requested_on}). You can't create a new request
                until the previous one is completed or rejected.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Check if any existing book request blocks creating a new one
  const hasExistingRequest = (): boolean => {
    if (!studentData?.bookRequests || !studentData.bookRequests.length) {
      return false;
    }

    const nextStageId = formData.stage_id + 1;

    return studentData.bookRequests.some(
      (request) =>
        request.stage_id === nextStageId &&
        (request.status === "pending" ||
          request.status === "approved" ||
          request.status === "completed")
    );
  };

  // Update form data when student data is loaded
  useEffect(() => {
    if (studentData) {
      const currentStage = studentData.course?.stage?.id || 0;

      setFormData({
        student_id: studentData.id,
        course_id: studentData.course?.id || 0,
        stage_id: currentStage,
        request_reason: "",
      });
    }
  }, [studentData]);

  // Refetch student data when component mounts
  useEffect(() => {
    if (studentId) {
      refetch();
    }
  }, [studentId, refetch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: BookRequestPayloadType = {
      student_id: formData.student_id,
      course_id: formData.course_id,
      stage_id: formData.stage_id + 1,
    };

    const result = await addBookRequest(payload);

    if (result.success) {
      showToast({
        title: "Success",
        text: "Book request created successfully",
        icon: "success",
      });
      navigate(-1);
    } else {
      showToast({
        title: "Error",
        text: result.error || "Failed to create book request",
        icon: "error",
      });
    }
  };

  // Get current stage name
  const getCurrentStageName = () => {
    const currentStageName = studentData?.course?.stage?.name || "";
    const currentStageNumber = parseInt(currentStageName.split(" ")[1] || "0");
    return `Level ${currentStageNumber + 1}`;
  };

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">Request Level Books</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.bookRequestList}>Book Requests</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Request Level Books
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <form onSubmit={handleSubmit}>
              {/* Warning Messages */}
              {!isStudentActive && (
                <div className="alert alert-danger mb-3" role="alert">
                  <div className="d-flex">
                    <i className="ti ti-alert-triangle mt-2 me-2"></i>
                    <div>
                      <h6 className="alert-heading mb-1">Inactive Student</h6>
                      <p className="mb-0">
                        Cannot request books for inactive students. Please
                        activate the student first.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {getBookRequestStatus()}

              {!hasInvoices && (
                <div className="alert alert-warning mb-3" role="alert">
                  <div className="d-flex">
                    <i className="ti ti-alert-triangle mt-2 me-2"></i>
                    <div>
                      <h6 className="alert-heading mb-1">No Invoices Found</h6>
                      <p className="mb-0">
                        Please generate invoices for the student before
                        requesting books.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {hasInvoices &&
                isSelectedStageStage1() &&
                !hasCompletedRegistrationFee && (
                  <div className="alert alert-warning mb-3" role="alert">
                    <div className="d-flex">
                      <i className="ti ti-alert-triangle mt-2 me-2"></i>
                      <div>
                        <h6 className="alert-heading mb-1">
                          Pending Registration Fee
                        </h6>
                        <p className="mb-0">
                          Registration fee must be paid before requesting books
                          for Level 1.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {hasInvoices &&
                !isSelectedStageStage1() &&
                !hasRequiredInvoices && (
                  <div className="alert alert-warning mb-3" role="alert">
                    <div className="d-flex">
                      <i className="ti ti-alert-triangle mt-2 me-2"></i>
                      <div>
                        <h6 className="alert-heading mb-1">
                          Pending Course Fee
                        </h6>
                        <p className="mb-0">
                          Course fee must be paid before requesting books for
                          this level.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {isStudentActive &&
                !isSelectedStageStage1() &&
                !hasMinimumAttendance && (
                  <div className="alert alert-warning mb-3" role="alert">
                    <div className="d-flex">
                      <i className="ti ti-alert-triangle mt-2 me-2"></i>
                      <div>
                        <h6 className="alert-heading mb-1">
                          Insufficient Attendance
                        </h6>
                        <p className="mb-0">
                          To request books for level{" "}
                          {parseInt(
                            studentData?.course?.stage?.name.split(" ")[1]
                          ) + 1}
                          , the student must have a minimum of 7 days of
                          attendance in {studentData?.course?.stage?.name}.
                          <br />
                          Total attendance recorded for{" "}
                          {studentData?.course?.stage?.name} : {attendanceCount}{" "}
                          days.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Student Information Section */}
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-user fs-16" />
                    </span>
                    <h4 className="text-dark">Student Information</h4>
                  </div>
                </div>
                <div className="card-body pb-1">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Student Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            studentData
                              ? studentData.name
                              : ""
                          }
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Student ID</label>
                        <input
                          type="text"
                          className="form-control"
                          value={studentData?.profile?.studentid || ""}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Attendance Count</label>
                        <input
                          type="text"
                          className={`form-control ${isSelectedStageStage1()
                            ? "text-muted"
                            : attendanceCount < 7
                              ? "text-warning"
                              : "text-success"
                            }`}
                          value={
                            isSelectedStageStage1()
                              ? "Not Required"
                              : attendanceCount
                          }
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Status</label>
                        <input
                          type="text"
                          className={`form-control ${!isStudentActive ? "text-danger" : "text-success"
                            }`}
                          value={studentData?.status || ""}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Registration Fee</label>
                        <input
                          type="text"
                          className={`form-control ${!isSelectedStageStage1()
                            ? "text-muted"
                            : !hasInvoices
                              ? "text-muted"
                              : studentData?.invoices?.some(
                                (inv) => inv.type === "registrationfee"
                              )
                                ? !hasCompletedRegistrationFee
                                  ? "text-warning"
                                  : "text-success"
                                : "text-muted"
                            }`}
                          value={
                            !isSelectedStageStage1()
                              ? "Not Required"
                              : !hasInvoices
                                ? "No Invoices"
                                : studentData?.invoices?.some(
                                  (inv) => inv.type === "registrationfee"
                                )
                                  ? hasCompletedRegistrationFee
                                    ? "Paid"
                                    : "Pending"
                                  : "Not Required"
                          }
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Course Fee ({studentData?.course?.stage?.name})
                        </label>
                        <input
                          type="text"
                          className={`form-control ${isSelectedStageStage1()
                            ? "text-muted"
                            : !hasInvoices
                              ? "text-muted"
                              : !hasCompletedCourseFee
                                ? "text-warning"
                                : "text-success"
                            }`}
                          value={
                            isSelectedStageStage1()
                              ? "Not Required"
                              : !hasInvoices
                                ? "No Invoices"
                                : hasCompletedCourseFee
                                  ? "Paid"
                                  : "Pending"
                          }
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Details Section */}
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-book fs-16" />
                    </span>
                    <h4 className="text-dark">Course Details</h4>
                  </div>
                </div>
                <div className="card-body pb-1">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Course</label>
                        <input
                          type="text"
                          className="form-control"
                          value={studentData?.course?.name || ""}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Requesting Books For
                        </label>
                        {studentData?.course?.stage ? (
                          <CommonSelect
                            className="select"
                            options={getStageOptions()}
                            value={getStageOptions()[0]}
                            isDisabled={true}
                            onChange={(option) => {
                              if (option && option.value !== "last") {
                                setFormData((prev) => ({
                                  ...prev,
                                  stage_id: parseInt(option.value, 10),
                                }));
                              }
                            }}
                            placeholder={getCurrentStageName()}
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            value={studentData?.course?.stage?.name || ""}
                            disabled
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="text-end">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn btn-light me-3"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    isLoading ||
                    !studentData ||
                    hasExistingRequest() ||
                    !isStudentActive ||
                    (!isSelectedStageStage1() && !hasMinimumAttendance) ||
                    !hasInvoices ||
                    (isSelectedStageStage1() && !hasCompletedRegistrationFee) ||
                    (!isSelectedStageStage1() && !hasRequiredInvoices)
                  }
                >
                  {isLoading ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBookRequest;
